/**
 * AIResearchResultsPage — Results page for AI image-based product classification.
 * Receives AIResearchResponse via route state and displays waste profile,
 * AI insights, image recognition signals, and captured product images.
 */

import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { Button, Divider, Icon, Thumbnail } from "@amzn/meridian";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import sparklesSmallTokens from "@amzn/meridian-tokens/base/icon/sparkles-small";
import viewTokens from "@amzn/meridian-tokens/base/icon/view";
import bulbTokens from "@amzn/meridian-tokens/base/icon/bulb";
import imageTokens from "@amzn/meridian-tokens/base/icon/image";
import searchTokens from "@amzn/meridian-tokens/base/icon/search";
import { AIResearchResponse } from "src/types/classificationTypes";
import CapturedImagesRow from "./CapturedImagesRow";
import PillButton from "../../UI/PillButton";
import "./classification.scss";

const AI_CLASSIFICATION_DISCLAIMER =
  "This classification is AI-assisted based on visual signals. Amazon Safety compliance " +
  "standards require manual review before final submission for non-catalog items.";

const InsightRow: React.FC<{ tokens: object; title: string; subtitle: string }> = ({ tokens, title, subtitle }) => (
  <Row alignmentVertical="center" spacing="400">
    <Icon tokens={tokens} color="success" />
    <Column spacing="100">
      <Text type="h100">{title}</Text>
      <Text type="b300" color="secondary">{subtitle}</Text>
    </Column>
  </Row>
);

const AIResearchResultsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const data = (location.state as { data?: AIResearchResponse })?.data;

  if (!data) {
    return (
      <Column width="100%" height="100vh" alignmentHorizontal="center" alignmentVertical="center" spacing="400">
        <Text>No classification data available.</Text>
        <PillButton text="Go Back" color="secondary" onClick={() => navigate("/home")} />
      </Column>
    );
  }

  const { site_code: siteCode, result, captured_image_urls: capturedImageUrls = [] } = data;
  const { image_recognition: recognition, waste_classification: classification } = result;
  const { ai_insight: insight } = classification;
  const matchedAsin = recognition.matched_catalogue_id || "Unidentified";

  return (
    <Column className="app-gradient-bg pwa-scrollable" spacing="400" spacingInset="500 500 600">
      <Row alignmentVertical="center" spacingInset="400 none">
        <Button type="icon" onClick={() => navigate(-1)}>
          <Icon color="success" tokens={arrowLeftTokens} />
        </Button>
        <Column spacing="100" spacingInset="200">
          <Text type="h100">ASIN {matchedAsin}</Text>
          <Text type="b200" color="secondary">SITE {siteCode}</Text>
        </Column>
      </Row>

      <Row spacing="400" spacingInset="200" alignmentVertical="center">
        {capturedImageUrls.length > 0 && (
          <Thumbnail source={capturedImageUrls[0]} size="large" type="square" />
        )}
        <Column spacing="100">
          <Text type="h200">{recognition.product_name || "Unknown Product"}</Text>
          <Text type="b200" color="secondary">
            Derived from visual analysis and external signals
          </Text>
        </Column>
      </Row>

      <Column spacing="400" spacingInset="200">
        <Text type="h100" color="secondary">WASTE PROFILE</Text>
        <Column className="home-card asin-waste-profile" spacing="none">
          <Row spacingInset="400" alignmentHorizontal="center">
            <Text type="h400" color="inverted">
              {classification.waste_profile.profile_code}
            </Text>
          </Row>
          <Column className="home-card" spacing="none" overflowY="hidden">
            <Column spacingInset="400">
              <Row alignmentVertical="center" spacing="200">
                <Icon color="success" tokens={sparklesSmallTokens} />
                <Text type="h100" color="success">AI INSIGHT</Text>
              </Row>
              <Text type="b300">{insight.summary}</Text>
            </Column>
            <Column className="ai-insight-details-bg" spacing="400" spacingInset="450">
              <InsightRow tokens={viewTokens} title="Visual Detection" subtitle={insight.visual_detection} />
              <Divider />
              <InsightRow tokens={bulbTokens} title="Ingredient Inference" subtitle={insight.ingredient_inference} />
            </Column>
          </Column>
        </Column>
      </Column>

      <Column spacing="400" spacingInset="200">
        <Text type="h100" color="secondary">SIGNALS USED FOR CLASSIFICATION</Text>
        <Column className="home-card" spacingInset="450" spacing="400">
          <InsightRow tokens={imageTokens} title="Image Features Extracted" subtitle={recognition.image_features.join(", ")} />
          <InsightRow tokens={searchTokens} title="Inferred Ingredients" subtitle={recognition.inferred_ingredients.join(", ")} />
        </Column>
      </Column>

      {capturedImageUrls.length > 0 && (
        <CapturedImagesRow
          images={capturedImageUrls}
          title="CAPTURED PRODUCT VIEWS"
          badgeLabel="ANALYZING"
        />
      )}

      <Column spacingInset="200">
        <Text type="b300" color="secondary">
          {AI_CLASSIFICATION_DISCLAIMER}
        </Text>
      </Column>

      {/* TODO: Navigate to choose container — next CR */}
      <Column className="asin-sticky-footer" spacing="300">
        <PillButton text="ADD TO CONTAINER" color="success" onClick={() => {}} />
      </Column>
    </Column>
  );
};

export default AIResearchResultsPage;
